import React from 'react';
import cn from 'classnames';

import styles from './styles.module.scss';

type Props = {
  progress: number;
  className?: string;
  theme?: string;
  /** 'thin' is the inline variant used next to the back button in the funnel header */
  size?: 'default' | 'thin';
  /**
   * Segmented rendering: one fill percentage per segment. When given, `progress` is
   * ignored — the caller has already turned it into per-segment fills.
   */
  fills?: number[];
  /** Segments to flash once, right after they have been completed. */
  flashIndexes?: number[];
};

// Ends of the single-bar gradient. Segments interpolate between them so the colour
// still runs continuously across the whole bar instead of repeating per segment.
const GRADIENT_FROM = { r: 255, g: 191, b: 0 };  // #FFBF00
const GRADIENT_TO = { r: 255, g: 66, b: 0 };     // #FF4200

function gradientColor(ratio: number): string {
  const channel = (from: number, to: number) => Math.round(from + (to - from) * ratio);

  return `rgb(${channel(GRADIENT_FROM.r, GRADIENT_TO.r)}, ${channel(GRADIENT_FROM.g, GRADIENT_TO.g)}, ${channel(GRADIENT_FROM.b, GRADIENT_TO.b)})`;
}

export default function SurveyProgressBar({ progress, className, theme = 'white', size = 'default', fills, flashIndexes }: Props) {
  if (fills && fills.length > 0) {
    return (
      <div className={cn(styles.segments, className, { [styles.thin]: size === 'thin' })}>
        {fills.map((fill, index) => (
          <div className={cn(styles.segment, { [styles.flash]: flashIndexes?.includes(index) })} key={index}>
            <div
              className={cn(styles.segmentProgress, { [styles.filled]: fill > 0 })}
              style={{
                width: `${fill}%`,
                background: `linear-gradient(95.82deg, ${gradientColor(index / fills.length)} 0%, ${gradientColor((index + 1) / fills.length)} 100%)`,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn(styles.surveyProgressBar, className, {
      [styles.darkTheme]: theme === 'dark',
      [styles.thin]: size === 'thin',
    })}>
      <div
        className={cn(styles.surveyProgressBarProgress, { [styles.filled]: progress > 0 })}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};
